import { useState, useEffect } from "react";
import { Marker, Circle, useMap } from "react-leaflet";

function readMapZoom(map) {
  return Math.round(map.getZoom());
}

/**
 * User crosshair + accuracy halo — hidden when zoomed out so clusters stay readable.
 */
export default function UserLocationMapLayers({
  position,
  minZoom,
  icon,
  pulsingIcon = null,
  pulseZoomMin = null,
  pulseZoomMax = null,
  accuracyRadius = null,
  accuracyPathOptions,
  markerZIndexOffset = 1000,
  markerInteractive = false,
  markerChildren = null,
}) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => readMapZoom(map));

  useEffect(() => {
    const syncZoom = () => setZoom(readMapZoom(map));
    syncZoom();
    map.on("zoom", syncZoom);
    map.on("zoomend", syncZoom);
    return () => {
      map.off("zoom", syncZoom);
      map.off("zoomend", syncZoom);
    };
  }, [map]);

  if (!position || zoom < minZoom) return null;

  const { lat, lng } = position;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const usePulse =
    pulsingIcon != null &&
    pulseZoomMin != null &&
    pulseZoomMax != null &&
    zoom >= pulseZoomMin &&
    zoom <= pulseZoomMax;
  const activeIcon = usePulse ? pulsingIcon : icon;

  return (
    <>
      {accuracyRadius != null && Number.isFinite(accuracyRadius) && (
        <Circle
          center={[lat, lng]}
          radius={accuracyRadius}
          pathOptions={accuracyPathOptions}
          interactive={false}
        />
      )}
      <Marker
        key={usePulse ? "user-loc-pulse" : "user-loc-static"}
        position={[lat, lng]}
        icon={activeIcon}
        interactive={markerInteractive}
        zIndexOffset={markerZIndexOffset}
      >
        {markerChildren}
      </Marker>
    </>
  );
}
